import Cookies from 'js-cookie';
import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Building2,
    ChevronDown,
    ClipboardList,
    KeyRound,
    LayoutDashboard,
    LogOut,
    MenuIcon,
    Radar,
    XIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/core/global-store';
import { toggleSidebar, toggleSidebarOnMobile } from '@/core/global-store/features/ui/uiSlice';
import {
    OWNER_SYSTEMS,
    OWNER_SYSTEM_SECTIONS,
    systemSectionPath,
    type OwnerSystemCode,
} from '@/features/control-plane/data/ownerSystems';

const OwnerSidebar = () => {
    const lng = Cookies.get('i18nextLng') || Cookies.get('i18next') || 'en';
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isSidebarOpen = useAppSelector((state) => state.ui.isSidebarOpen);
    const isSidebarOpenOnMobile = useAppSelector((state) => state.ui.isSidebarOpenOnMobile);
    const user = useAppSelector((state) => state.auth.me);
    const expanded = isSidebarOpen || isSidebarOpenOnMobile;

    const activeSystem = useMemo(() => {
        const m = location.pathname.match(/\/control-plane\/systems\/([^/]+)/);
        return (m?.[1] as OwnerSystemCode | undefined) ?? null;
    }, [location.pathname]);

    const [openSystems, setOpenSystems] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (activeSystem) {
            setOpenSystems((prev) => ({ ...prev, [activeSystem]: true }));
        }
    }, [activeSystem]);

    // Keep owner sidebar expanded on desktop so systems stay discoverable.
    useEffect(() => {
        if (!isSidebarOpen && typeof window !== 'undefined' && window.innerWidth >= 768) {
            dispatch(toggleSidebar());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggle = () => dispatch(toggleSidebar());
    const toggleMobile = () => dispatch(toggleSidebarOnMobile());
    const closeMobile = () => {
        if (isSidebarOpenOnMobile) toggleMobile();
    };

    const handleLogout = () => {
        Cookies.remove('accessToken');
        toast.success(t('logout_success'), { position: 'top-right', duration: 3000 });
        navigate('/login');
        closeMobile();
    };

    const linkClass = ({ isActive }: { isActive: boolean }) =>
        cn(
            'group flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm transition-colors duration-200 cursor-pointer',
            isActive
                ? 'bg-sky-600 text-white shadow-sm'
                : 'text-slate-300 hover:bg-slate-800 hover:text-white',
        );

    const childLinkClass = ({ isActive }: { isActive: boolean }) =>
        cn(
            'block rounded-md px-2.5 py-1.5 text-[13px] transition-colors duration-200 cursor-pointer',
            isActive ? 'bg-sky-700/80 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100',
        );

    return (
        <>
            <button
                type="button"
                className="fixed end-4 top-4 z-50 rounded-lg bg-slate-900 p-2 text-white md:hidden"
                onClick={toggleMobile}
                aria-label="Toggle menu"
            >
                {isSidebarOpenOnMobile ? <XIcon className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
            </button>

            <aside
                className={cn(
                    'fixed start-0 top-0 z-40 flex h-full flex-col bg-[#0F172A] text-slate-100 shadow-xl transition-all duration-300 ease-in-out font-[family-name:var(--font-owner,inherit)]',
                    {
                        'w-full translate-x-0': isSidebarOpenOnMobile,
                        'w-0 overflow-hidden md:sticky md:top-0 md:h-screen md:w-[272px] md:overflow-visible':
                            !isSidebarOpenOnMobile && isSidebarOpen,
                        'w-0 overflow-hidden md:sticky md:top-0 md:h-screen md:w-[72px] md:overflow-visible':
                            !isSidebarOpenOnMobile && !isSidebarOpen,
                        'translate-x-full md:translate-x-0': !isSidebarOpenOnMobile && lng === 'ar',
                        '-translate-x-full md:translate-x-0': !isSidebarOpenOnMobile && lng !== 'ar',
                    },
                )}
            >
                <div className="flex h-14 items-center justify-between border-b border-slate-800 px-3">
                    <button
                        type="button"
                        className="flex min-w-0 items-center gap-2 cursor-pointer"
                        onClick={() => {
                            navigate('/control-plane/overview');
                            closeMobile();
                        }}
                    >
                        {user?.data?.company_logo ? (
                            <img
                                src={user.data.company_logo}
                                alt=""
                                className="h-8 w-8 shrink-0 rounded object-contain bg-white/10 p-0.5"
                            />
                        ) : (
                            <div className="flex h-8 w-8 items-center justify-center rounded bg-sky-700 text-xs font-bold">
                                MRN
                            </div>
                        )}
                        {expanded ? (
                            <div className="min-w-0 text-start">
                                <div className="truncate text-sm font-semibold tracking-tight">MRN Control</div>
                                <div className="truncate text-[11px] text-slate-400">{t('controlPlane.ownerBadge')}</div>
                            </div>
                        ) : null}
                    </button>
                    <button
                        type="button"
                        onClick={toggle}
                        className="hidden rounded p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white md:inline-flex cursor-pointer"
                        aria-label="Collapse sidebar"
                    >
                        <ChevronDown className={cn('h-4 w-4 transition', isSidebarOpen ? 'rotate-90' : '-rotate-90')} />
                    </button>
                </div>

                <nav className="custom-scrollbar flex-1 space-y-4 overflow-y-auto px-2.5 py-3">
                    <div className="space-y-0.5">
                        {expanded ? (
                            <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                {t('controlPlane.navPlatform')}
                            </p>
                        ) : null}
                        <NavLink to="/control-plane/overview" className={linkClass} onClick={closeMobile} end>
                            <LayoutDashboard className="h-4 w-4 shrink-0" />
                            {expanded ? <span>{t('controlPlane.overview')}</span> : null}
                        </NavLink>
                        <NavLink to="/control-plane/registrations" className={linkClass} onClick={closeMobile}>
                            <ClipboardList className="h-4 w-4 shrink-0" />
                            {expanded ? <span>{t('controlPlane.websiteApplications')}</span> : null}
                        </NavLink>
                        <NavLink to="/control-plane/companies" className={linkClass} onClick={closeMobile}>
                            <Building2 className="h-4 w-4 shrink-0" />
                            {expanded ? <span>{t('controlPlane.companies')}</span> : null}
                        </NavLink>
                    </div>

                    <div className="space-y-1">
                        {expanded ? (
                            <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                {t('controlPlane.navSystems')}
                            </p>
                        ) : null}
                        {OWNER_SYSTEMS.map((system) => {
                            const open = !!openSystems[system.code];
                            const isActiveParent = activeSystem === system.code;
                            return (
                                <div key={system.code} className="rounded-lg">
                                    <button
                                        type="button"
                                        className={cn(
                                            'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm cursor-pointer transition-colors duration-200',
                                            isActiveParent
                                                ? 'bg-slate-800 text-white'
                                                : 'text-slate-300 hover:bg-slate-800 hover:text-white',
                                        )}
                                        onClick={() => {
                                            if (!expanded) {
                                                navigate(systemSectionPath(system.code, 'control'));
                                                return;
                                            }
                                            setOpenSystems((prev) => ({
                                                ...prev,
                                                [system.code]: !prev[system.code],
                                            }));
                                        }}
                                    >
                                        <span
                                            className="h-2 w-2 shrink-0 rounded-full"
                                            style={{ backgroundColor: system.accent }}
                                            aria-hidden
                                        />
                                        {expanded ? (
                                            <>
                                                <span className="flex-1 text-start font-medium">{t(system.nameKey)}</span>
                                                <ChevronDown
                                                    className={cn('h-3.5 w-3.5 text-slate-500 transition', open && 'rotate-180')}
                                                />
                                            </>
                                        ) : null}
                                    </button>
                                    {expanded && open ? (
                                        <div className="ms-3 space-y-0.5 border-s border-slate-700 py-1 ps-2">
                                            {OWNER_SYSTEM_SECTIONS.map((section) => (
                                                <NavLink
                                                    key={section.id}
                                                    to={systemSectionPath(system.code, section.id)}
                                                    className={childLinkClass}
                                                    onClick={closeMobile}
                                                >
                                                    {t(section.nameKey)}
                                                </NavLink>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>

                    <div className="space-y-0.5">
                        {expanded ? (
                            <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                {t('controlPlane.navOps')}
                            </p>
                        ) : null}
                        <NavLink to="/control-plane/launch-audit" className={linkClass} onClick={closeMobile}>
                            <Radar className="h-4 w-4 shrink-0" />
                            {expanded ? <span>{t('controlPlane.launchAudit')}</span> : null}
                        </NavLink>
                        <NavLink to="/control-plane/roles" className={linkClass} onClick={closeMobile}>
                            <KeyRound className="h-4 w-4 shrink-0" />
                            {expanded ? <span>{t('controlPlane.roles')}</span> : null}
                        </NavLink>
                    </div>
                </nav>

                <div className="border-t border-slate-800 p-2.5">
                    <button
                        type="button"
                        onClick={handleLogout}
                        className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-slate-300 transition hover:bg-slate-800 hover:text-white"
                    >
                        <LogOut className="h-4 w-4" />
                        {expanded ? <span>{t('logout')}</span> : null}
                    </button>
                </div>
            </aside>
        </>
    );
};

export default OwnerSidebar;
